import { Badge } from '@@/Badge';

import { K8sGPUSummary } from './queries/useGpuQuery';

interface Props {
  summary: K8sGPUSummary;
}

export function GpuSummary({ summary }: Props) {
  const readinessStatus = getReadinessStatus(summary);

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex items-center gap-x-2">
        <span className="font-semibold">Readiness:</span>
        <Badge type={readinessBadgeType(readinessStatus)}>
          {readinessStatus}
        </Badge>
      </div>

      <table className="table-hover table-sm table">
        <thead>
          <tr>
            <th>Resource Type</th>
            <th>Capacity</th>
            <th>Allocatable</th>
            <th>Allocated</th>
            <th>Available</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(summary.TotalCapacity).map((resourceType) => {
            const capacity = summary.TotalCapacity[resourceType] ?? 0;
            const allocatable = summary.TotalAllocatable[resourceType] ?? 0;
            const allocated = summary.TotalAllocated[resourceType] ?? 0;
            const available = allocatable - allocated;
            return (
              <tr key={resourceType}>
                <td>{resourceType}</td>
                <td>{capacity}</td>
                <td>{allocatable}</td>
                <td>{allocated}</td>
                <td>{available}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <span>
          <span className="font-semibold">GPU-capable nodes:</span>{' '}
          {summary.GPUNodeCount}
        </span>
        <span>
          <span className="font-semibold">Degraded nodes:</span>{' '}
          {summary.DegradedCount}
        </span>
        {summary.OperatorDetected && (
          <Badge type="info">GPU Operator detected</Badge>
        )}
        {summary.DevicePluginDetected && (
          <Badge type="info">Device plugin detected</Badge>
        )}
      </div>

      <p className="text-muted text-sm">
        GPU health is inferred from Kubernetes signals only (node readiness,
        resource availability, scheduling success). Hardware utilization and
        temperature are not measured.
      </p>
    </div>
  );
}

type ReadinessStatus = 'Ready' | 'Degraded' | 'Not Available';

function getReadinessStatus(summary: K8sGPUSummary): ReadinessStatus {
  if (summary.GPUNodeCount === 0) {
    return 'Not Available';
  }
  if (summary.DegradedCount > 0) {
    return 'Degraded';
  }
  return 'Ready';
}

function readinessBadgeType(
  status: ReadinessStatus
): 'success' | 'warn' | 'muted' {
  switch (status) {
    case 'Ready':
      return 'success';
    case 'Degraded':
      return 'warn';
    default:
      return 'muted';
  }
}
